<template>
  <q-card
    v-if="showing && showing.isRemote"
    flat
    square
    class="pointer-cursor showing published"
    :style="colorStyle"
    @click="$emit('click')"
  >
    <q-card-section
      horizontal
    >
      <img
        v-if="showPosters"
        class="poster-img"
        :class="showing && showing.published ? 'poster-img-small' : ''"
        :src="$q.screen.lg ? showing.movie && showing.movie.bannerImageUrl : showing.movie && showing.movie.posterImageSmallUrl"
      >
      <q-card-section class="q-pa-xs overflow-hidden">
        <q-card-section class="q-px-xs q-py-none ellipsis text-bold text-left">
          {{ showing.movie && showing.movie.name }}
        </q-card-section>
        <q-card-section
          class="q-pl-none q-pr-xs q-py-none ellipsis text-left"
        >
          <q-chip
            v-if="showRelativeTimeChips && relativeStartTime"
            class="text-no-wrap ellipsis"
            size="sm"
            dense
            square
            color="green"
            text-color="white"
          >
            {{ relativeStartTime }}
          </q-chip>
          <q-chip
            v-else-if="showRelativeTimeChips && relativeEndTime"
            class="text-no-wrap ellipsis"
            size="sm"
            dense
            square
            color="red"
            text-color="white"
          >
            {{ relativeEndTime }}
          </q-chip>
          <q-chip
            v-if="showTimeChips"
            class="text-no-wrap ellipsis"
            size="sm"
            dense
            square
            color="white"
            text-color="dark"
          >
            {{ startTime }} - {{ endTime }}
          </q-chip>
          <rated
            v-if="showRating && showing.movie.rating"
            :outline="false"
            :color="'secondary'"
            :rated="showing.movie.rating"
            :size="'sm'"
            :rating-reason="showing.movie.ratingReason"
            dense
          />
          <q-chip
            v-if="showPriceCard"
            size="sm"
            dense
            color="primary"
            text-color="white"
          >
            {{ showing.priceCard && showing.priceCard.name }}
          </q-chip>
          <q-chip
            v-if="showSeatChart && showing.overrideSeatChart && showing.customSeatChart"
            size="xs"
            square
          >
            <q-avatar
              icon="event_seat"
              color="red"
              text-color="white"
            />
            {{ showing.customSeatChart.name }}
          </q-chip>
          <showing-badge
            v-for="showingBadge in (showShowingBadges ? showing.showingBadges.data : [])"
            :key="showingBadge.id"
            :showing-badge="showingBadge"
            size="xs"
            outline
            :color="$q.dark.isActive ? 'white' : 'black'"
          />
          <q-space />
        </q-card-section>
      </q-card-section>
      <q-space />
      <div
        class="text-center q-px-xs"
        style="padding-top: 3px;"
      >
        <div
          class="text-h6 text-center"
          style="height: 20px;"
        >
          {{ showing.ticketsSold }}
        </div>
        <div
          class="text-subtitle2 text-center q-pt-xs"
          style="white-space: nowrap; font-size: 12px;"
        >
          / {{ showing.screen.seatCount }}&nbsp;
        </div>
      </div>
      <q-icon
        v-if="showing && showing.private"
        name="visibility_off"
        color="negative"
        style="position: absolute; top: 22px; right: 3px;"
      >
        <q-tooltip>
          This showing is private and will not show up on the website
        </q-tooltip>
      </q-icon>
    </q-card-section>

    <q-chip
      v-if="showTimes && !relativeStartTime"
      size="sm"
      dense
      color="green"
      text-color="white"
      class="text-no-wrap ellipsis"
      style="position: absolute; bottom: -23px; left: -8px; border-radius: 0px 0px 5px 5px"
    >
      {{ startTime }}
    </q-chip>
    <q-chip
      v-if="showTimes && !relativeEndTime && showing.totalDurationMinutes > 15"
      size="sm"
      dense
      color="red"
      text-color="white"
      class="text-no-wrap ellipsis"
      style="position: absolute; bottom: -23px; right: -8px; border-radius: 0px 0px 5px 5px"
    >
      {{ endTime }}
    </q-chip>

    <q-chip
      v-if="showTimes && relativeStartTime"
      class="text-no-wrap ellipsis"
      size="sm"
      dense
      color="green"
      text-color="white"
      style="position: absolute; bottom: -23px; left: -8px; border-radius: 0px 0px 5px 5px"
    >
      {{ relativeStartTime }}
    </q-chip>
    <q-chip
      v-if="showTimes && relativeEndTime && showing.totalDurationMinutes > 15"
      class="text-no-wrap ellipsis"
      size="sm"
      dense
      color="red"
      text-color="white"
      style="position: absolute; bottom: -23px; right: -8px; border-radius: 0px 0px 5px 5px"
    >
      {{ relativeEndTime }}
    </q-chip>

    <showing-progress-bar
      v-if="showProgressBar"
      style="position: relative; top: -4px;"
      :showing="showing"
    />
  </q-card>
  <div
    v-else
    style="height: 60px;"
    class="row justify-center items-center"
  >
    <q-spinner
      class="col text-grey"
      size="sm"
    />
  </div>
</template>

<script>

import { date } from 'quasar'
import { timeToMinutes } from 'src/models/schedule/TimeSlot'
import Showing from 'src/models/showing/Showing'
import ShowingProgressBar from 'src/modules/admin/report/ShowingProgressBar'
import ShowingBadge from 'src/modules/admin/showing-badge/components/ShowingBadge'
import Rated from 'src/modules/consumer/components/Rated'

export default {
  name: 'ShowingSlot',
  components: {
    ShowingBadge,
    ShowingProgressBar,
    Rated
  },
  props: {
    showing: {
      type: Showing,
      required: true
    },
    showTimes: {
      type: Boolean,
      required: false,
      default: true
    },
    showTimeChips: {
      type: Boolean,
      required: false,
      default: true
    },
    showRating: {
      type: Boolean,
      required: false,
      default: true
    },
    showSeatChart: {
      type: Boolean,
      required: false,
      default: true
    },
    showPriceCard: {
      type: Boolean,
      required: false,
      default: true
    },
    showShowingBadges: {
      type: Boolean,
      required: false,
      default: true
    },
    showPosters: {
      type: Boolean,
      required: false,
      default: true
    },
    showProgressBar: {
      type: Boolean,
      required: false,
      default: true
    },
    showRelativeTimes: {
      type: Boolean,
      required: false,
      default: true
    },
    showRelativeTimeChips: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  configuration: ['currentSite'],
  emits: ['click'],
  computed: {
    startTime () {
      return date.formatDate(this.showing.siteTime, 'h:mmA').slice(0, -1)
    },
    startTimeMinutes () {
      return timeToMinutes(this.showing.siteTime)
    },
    endTime () {
      const time = date.addToDate(this.showing.siteTime, { minutes: this.showing.totalDurationMinutes })
      return date.formatDate(time, 'h:mmA').slice(0, -1)
    },
    endTimeMinutes () {
      const time = date.addToDate(this.showing.siteTime, { minutes: this.showing.totalDurationMinutes })
      return timeToMinutes(time)
    },
    color () {
      return this.showing.movie?.color || '#444'
    },
    colorStyle () {
      return this.colorStyleHelper(this.color)
    },
    relativeStartTime () {
      const time = this.showRelativeTimes &&
        this.showing.siteTime < date.addToDate(this.currentSite.siteTime, { minutes: 60 }) &&
        this.showing.siteTime > date.addToDate(this.currentSite.siteTime, { minutes: -60 }) &&
        this.showing.siteTime.relativeTime({ compact: true, relativeTo: this.currentSite.siteTime })

      return time ? `Start${_.endsWith(time, 'ago') ? 'ed' : 's in'} ${time}` : false
    },
    relativeEndTime () {
      const time = this.showRelativeTimes &&
        this.showing.siteEndTime < date.addToDate(this.currentSite.siteTime, { minutes: 60 }) &&
        this.showing.siteEndTime > date.addToDate(this.currentSite.siteTime, { minutes: -60 }) &&
        this.showing.siteEndTime.relativeTime({ compact: true, relativeTo: this.currentSite.siteTime })

      return time ? `End${_.endsWith(time, 'ago') ? 'ed' : 's in'} ${time}` : false
    }
  },
  methods: {
    colorStyleHelper (color) {
      return 'color: ' + color + '; border-color: ' + color + '; background-color: ' + color + '33;'
    }
  }
}

</script>

<style lang="scss" scoped>
  .poster-img {
    width: 40px;
    height: 56px;
  }

  .poster-img-small {
    width: 36px;
    height: 52px;
  }

  .showing {
    border: solid 2px;
    height: 60px;
    background: white;
    z-index: 4;
  }

  .published {
    border-width: 4px;
  }

  .poster-wrapper {
    width: 40px;
    height: 60px;
    overflow: hidden;
  }
</style>
